import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, Mail, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const OrderConfirmationPage = () => {
  const location = useLocation();
  const { orderId } = useParams();
  const order = location.state?.order;

  return (
    <div className="min-h-screen section-spacing">
      <div className="container-handcraft">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto text-center"
        >
          <div className="w-20 h-20 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-sage" />
          </div>

          <p className="font-handwritten text-3xl text-accent mb-4">Thank You!</p>
          <h1 className="font-serif text-3xl md:text-4xl text-charcoal mb-4">
            Your Order Has Been Placed
          </h1>
          
          {order && (
            <p className="text-lg text-muted-foreground mb-2">
              Order Number: <span className="font-semibold text-charcoal">{order.order_number}</span>
            </p>
          )}

          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            We've received your order and will begin processing it shortly. 
            You'll receive a confirmation email with all the details.
          </p>

          {/* Order Details */}
          {order && (
            <div className="bg-white rounded-2xl border border-sand p-6 md:p-8 text-left mb-8">
              <h2 className="font-serif text-xl text-charcoal mb-6">Order Details</h2>
              
              <div className="space-y-4 mb-6">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between items-start py-3 border-b border-sand last:border-0">
                    <div>
                      <p className="font-medium text-charcoal">{item.product_name}</p>
                      <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                      {item.is_custom && (
                        <span className="badge-handcraft bg-sage/20 text-sage text-xs mt-1">Custom Order</span>
                      )}
                    </div>
                    <p className="font-medium">₹{item.total?.toLocaleString()}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-sand pt-4">
                <div className="flex justify-between text-lg font-semibold text-charcoal">
                  <span>Total</span>
                  <span>₹{order.total?.toLocaleString()}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-sand">
                <h3 className="font-medium text-charcoal mb-2">Shipping Address</h3>
                <p className="text-muted-foreground text-sm">
                  {order.customer?.name}<br />
                  {order.customer?.address}<br />
                  {order.customer?.city}, {order.customer?.state} - {order.customer?.pincode}<br />
                  Phone: {order.customer?.phone}
                </p>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-muted/50 rounded-xl p-6 text-left">
              <Mail className="w-8 h-8 text-accent mb-3" />
              <h3 className="font-medium text-charcoal mb-1">Check Your Email</h3>
              <p className="text-sm text-muted-foreground">
                Order confirmation sent to your email address.
              </p>
            </div>
            <div className="bg-muted/50 rounded-xl p-6 text-left">
              <Package className="w-8 h-8 text-accent mb-3" />
              <h3 className="font-medium text-charcoal mb-1">Order Processing</h3>
              <p className="text-sm text-muted-foreground">
                We'll notify you when your order ships.
              </p>
            </div>
          </div>

          <Link to="/shop">
            <Button className="btn-handcraft bg-charcoal text-cream" data-testid="continue-shopping">
              Continue Shopping
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
